import React, { useState, useEffect, useCallback } from 'react';
import { PlusIcon, PencilIcon, TrashIcon, ArrowPathIcon, CheckCircleIcon, XCircleIcon, MapPinIcon, StarIcon, HomeIcon } from '@heroicons/react/24/outline';
import apiService, { Address } from '../services/api';
import SimpleLocationSelector from './SimpleLocationSelector';

interface AddressManagerProps {
  userName?: string;
  onAddressChange?: (addresses: Address[]) => void;
  showDeliveryInfo?: boolean; // Control whether to show delivery fee info
}

const AddressManager: React.FC<AddressManagerProps> = ({ userName, onAddressChange, showDeliveryInfo }) => {
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingAddress, setEditingAddress] = useState<Address | null>(null);
  const [formData, setFormData] = useState<Partial<Address>>({
    name: '',
    company: '',
    phone: '',
    address_line_1: '',
    address_line_2: '',
    township: '',
    state_region: '',
    postal_code: '',
    country: 'Myanmar',
    is_default: false
  });
  const [loading, setLoading] = useState(true);

  const [showToast, setShowToast] = useState<{ message: string; type: 'success' | 'error'; title: string } | null>(null);
  const [actionStates, setActionStates] = useState<Record<string, boolean>>({});

  const showToastMessage = (title: string, message: string, type: 'success' | 'error') => {
    setShowToast({ title, message, type });
    setTimeout(() => setShowToast(null), 5000);
  };

  // Auto-hide toast after 3 seconds
  useEffect(() => {
    if (showToast) {
      const timer = setTimeout(() => setShowToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [showToast]);

  useEffect(() => {
    const fetchAddresses = async () => {
      setLoading(true);
      try {
        const fetchedAddresses = await apiService.getAddresses();
        setAddresses(fetchedAddresses);
      } catch (err: any) {
        const errorMessage = err.response?.data?.message || 'Failed to fetch addresses.';
        showToastMessage('Error Occurred', errorMessage, 'error');
      } finally {
        setLoading(false);
      }
    };
    fetchAddresses();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  // Memoized callback functions to prevent re-renders
  const handleStateRegionChange = useCallback((value: string) => {
    setFormData((prev) => ({ ...prev, state_region: value }));
  }, []);

  const handleTownshipChange = useCallback((value: string) => {
    setFormData((prev) => ({ ...prev, township: value }));
  }, []);

  const hideForm = () => {
    setShowForm(false);
    setEditingAddress(null);
    setFormData({
      name: '',
      company: '',
      phone: '',
      address_line_1: '',
      address_line_2: '',
      township: '',
      state_region: '',
      postal_code: '',
      country: 'Myanmar',
      is_default: false
    });
  };

  const handleEdit = (address: Address) => {
    setEditingAddress(address);
    setFormData(address);
    setShowForm(true);
  };

  const handleAddNew = () => {
    setEditingAddress(null);
    setFormData({
      name: userName || '',
      company: '',
      phone: '',
      address_line_1: '',
      address_line_2: '',
      township: '',
      state_region: '',
      postal_code: '',
      country: 'Myanmar',
      is_default: !addresses.some(a => a.is_default)
    });
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const isSaving = true;
    setActionStates(prev => ({ ...prev, save: isSaving }));

    // Ensure required fields are present
    const addressData = {
      name: formData.name || '',
      phone: formData.phone || '',
      company: formData.company || '',
      address_line_1: formData.address_line_1 || '',
      address_line_2: formData.address_line_2 || '',
      township: formData.township || '',
      state_region: formData.state_region || '',
      postal_code: formData.postal_code || '',
      country: formData.country || 'Myanmar',
      is_default: formData.is_default || false
    };

    try {
      if (editingAddress) {
        const updatedAddress = await apiService.updateAddress(editingAddress.id, addressData);
        setAddresses(addresses.map(a => a.id === editingAddress.id ? updatedAddress : a));
        showToastMessage('Address Updated', 'Your address has been updated successfully.', 'success');
      } else {
        const newAddress = await apiService.createAddress(addressData);
        setAddresses([...addresses, newAddress]);
        showToastMessage('Address Added', 'Your new address has been added successfully.', 'success');
      }
      hideForm();
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to save address.';
      showToastMessage('Error Occurred', errorMessage, 'error');
    } finally {
      setActionStates(prev => ({ ...prev, save: false }));
    }
  };

  const handleDelete = async (id: number) => {
    setActionStates(prev => ({ ...prev, [`delete_${id}`]: true }));

    try {
      await apiService.deleteAddress(id);
      setAddresses(addresses.filter(a => a.id !== id));
      showToastMessage('Address Deleted', 'The address has been deleted successfully.', 'success');
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to delete address.';
      showToastMessage('Error Occurred', errorMessage, 'error');
    } finally {
      setActionStates(prev => ({ ...prev, [`delete_${id}`]: false }));
    }
  };

  const handleSetDefault = async (id: number) => {
    setActionStates(prev => ({ ...prev, [`default_${id}`]: true }));

    try {
      await apiService.setDefaultAddress(id);
      setAddresses(addresses.map(a => ({ ...a, is_default: a.id === id })));
      showToastMessage('Default Address Set', 'The address has been set as default successfully.', 'success');
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to set default address.';
      showToastMessage('Error Occurred', errorMessage, 'error');
    } finally {
      setActionStates(prev => ({ ...prev, [`default_${id}`]: false }));
    }
  };

  if (loading) {
    return (<div className="flex justify-center items-center p-8"><ArrowPathIcon className="animate-spin w-8 h-8 text-gray-500" /></div>);
  }

  return (
    <div className="p-8 bg-gray-50 min-h-screen">
      {/* Premium Toast Notification */}
      {showToast && (
        <div className="fixed top-6 right-6 z-50 animate-in transform transition-all duration-500 ease-out">
          <div className="bg-gray-900 rounded-2xl shadow-2xl border border-gray-700 p-6 max-w-sm backdrop-blur-sm">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center shadow-lg ring-2 ring-white/20 ring-offset-2 ring-offset-gray-900 ${
                  showToast.type === 'success' 
                    ? 'bg-gradient-to-br from-green-500 via-emerald-500 to-teal-500' 
                    : 'bg-gradient-to-br from-red-500 via-rose-500 to-pink-500'
                }`}>
                  {showToast.type === 'success' ? (
                    <CheckCircleIcon className="w-7 h-7 text-white" />
                  ) : (
                    <XCircleIcon className="w-7 h-7 text-white" />
                  )}
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-semibold text-white">{showToast.title}</h3>
                  <button
                    onClick={() => setShowToast(null)}
                    className="text-gray-400 hover:text-white transition-colors duration-200 p-1 hover:bg-white/10 rounded-full"
                  >
                    <XCircleIcon className="w-5 h-5" />
                  </button>
                </div>
                <p className="text-sm text-gray-300 mt-1 leading-relaxed">
                  {showToast.message}
                </p>
                <div className={`mt-4 flex items-center text-xs font-medium px-3 py-2 rounded-lg border ${
                  showToast.type === 'success'
                    ? 'text-green-200 bg-gradient-to-r from-green-500/20 to-emerald-500/20 border-green-500/30'
                    : 'text-red-200 bg-gradient-to-r from-red-500/20 to-rose-500/20 border-red-500/30'
                }`}>
                  <div className={`w-2 h-2 rounded-full mr-2 animate-pulse ${
                    showToast.type === 'success' ? 'bg-green-400' : 'bg-red-400'
                  }`}></div>
                  <span>{showToast.type === 'success' ? 'Changes saved automatically' : 'Please try again'}</span>
                  {showToast.type === 'success' ? (
                    <CheckCircleIcon className="w-3 h-3 ml-2 text-green-400" />
                  ) : (
                    <XCircleIcon className="w-3 h-3 ml-2 text-red-400" />
                  )}
                </div>
              </div>
            </div>
            {/* Animated Progress Bar */}
            <div className="mt-5 w-full bg-gray-700 rounded-full h-1.5 overflow-hidden">
              <div className={`h-full rounded-full animate-progress shadow-sm ${
                showToast.type === 'success'
                  ? 'bg-gradient-to-r from-green-500 via-emerald-500 to-teal-500'
                  : 'bg-gradient-to-r from-red-500 via-rose-500 to-pink-500'
              }`}></div>
            </div>
            {/* Subtle glow effect */}
            <div className={`absolute inset-0 rounded-2xl pointer-events-none ${
              showToast.type === 'success'
                ? 'bg-gradient-to-r from-green-500/10 to-emerald-500/10'
                : 'bg-gradient-to-r from-red-500/10 to-rose-500/10'
            }`}></div>
          </div>
        </div>
      )}
      <div className="max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-lg sm:text-2xl font-bold text-gray-800 flex items-center">
            <MapPinIcon className="w-5 h-5 sm:w-7 sm:h-7 mr-2 sm:mr-3"/>
            <span className="text-base sm:text-2xl">My Addresses</span>
          </h2>
          {!showForm && (
            <button onClick={handleAddNew} className="flex items-center bg-gray-800 text-white px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg hover:bg-gray-700 transition-colors shadow-sm text-xs sm:text-sm">
              <PlusIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
              <span className="hidden xs:inline">Add New </span>Address
            </button>
          )}
        </div>


        {showForm ? (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-8 py-6 border-b border-gray-200">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-gray-800 rounded-xl flex items-center justify-center mr-4">
                  <MapPinIcon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">{editingAddress ? 'Edit Address' : 'Add New Address'}</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {editingAddress ? 'Update your delivery information' : 'Add a new delivery address to your account'}
                  </p>
                </div>
              </div>
            </div>
            
            <form onSubmit={handleSubmit} className="p-8">
              <div className="space-y-8">
                {/* Personal Information Section */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <PencilIcon className="w-5 h-5 mr-2 text-gray-600" />
                    Personal Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">Full Name</label>
                      <div className="relative">
                        <input 
                          name="name" 
                          value={formData.name || ''} 
                          onChange={handleInputChange} 
                          placeholder="Enter your full name" 
                          className={`block w-full px-4 py-3 rounded-xl border-2 transition-all duration-200 ${
                            !editingAddress 
                              ? 'bg-gray-50 border-gray-200 text-gray-700 cursor-not-allowed' 
                              : 'border-gray-200 focus:border-gray-400 focus:ring-4 focus:ring-gray-100'
                          }`}
                          required 
                          readOnly={!editingAddress} 
                        />
                        {!editingAddress && (
                          <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                            <span className="text-xs text-gray-500 bg-gray-200 px-2 py-1 rounded-full">Auto-filled</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">Phone Number *</label>
                      <input 
                        name="phone" 
                        value={formData.phone || ''} 
                        onChange={handleInputChange} 
                        placeholder="Enter your phone number" 
                        className="block w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-gray-400 focus:ring-4 focus:ring-gray-100 transition-all duration-200"
                        required 
                      />
                    </div>
                  </div>
                </div>

                {/* Address Information Section */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <MapPinIcon className="w-5 h-5 mr-2 text-gray-600" />
                    Address Information
                  </h4>
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">Address Line 1 *</label>
                        <input 
                          name="address_line_1" 
                          value={formData.address_line_1 || ''} 
                          onChange={handleInputChange} 
                          placeholder="Street address, building name" 
                          className="block w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-gray-400 focus:ring-4 focus:ring-gray-100 transition-all duration-200"
                          required 
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">Address Line 2</label>
                        <input 
                          name="address_line_2" 
                          value={formData.address_line_2 || ''} 
                          onChange={handleInputChange} 
                          placeholder="Apartment, suite, unit (optional)" 
                          className="block w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-gray-400 focus:ring-4 focus:ring-gray-100 transition-all duration-200"
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="md:col-span-1">
                        <SimpleLocationSelector
                          selectedStateRegion={formData.state_region || ''}
                          selectedTownship={formData.township || ''}
                          onStateRegionChange={handleStateRegionChange}
                          onTownshipChange={handleTownshipChange}
                          disabled={actionStates.save}
                          showDeliveryInfo={showDeliveryInfo}
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">Postal Code</label>
                        <input 
                          name="postal_code" 
                          value={formData.postal_code || ''} 
                          onChange={handleInputChange} 
                          placeholder="Enter postal code" 
                          className="block w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-gray-400 focus:ring-4 focus:ring-gray-100 transition-all duration-200"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">Country *</label>
                      <input 
                        name="country" 
                        value={formData.country || 'Myanmar'} 
                        onChange={handleInputChange} 
                        placeholder="Enter country" 
                        className="block w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-gray-400 focus:ring-4 focus:ring-gray-100 transition-all duration-200"
                        required 
                      />
                    </div>
                  </div>
                </div>

                {/* Preferences Section */}
                <div className="bg-gray-50 rounded-xl p-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Preferences</h4>
                  <div className="flex items-start space-x-3">
                    <input 
                      type="checkbox" 
                      name="is_default" 
                      id="is_default" 
                      checked={formData.is_default || false} 
                      onChange={handleInputChange} 
                      className="h-5 w-5 text-gray-800 border-2 border-gray-300 rounded focus:ring-gray-500 focus:ring-2 mt-0.5"
                    />
                    <div>
                      <label htmlFor="is_default" className="block text-sm font-medium text-gray-900">
                        Set as default address
                      </label>
                      <p className="text-sm text-gray-600 mt-1">
                        This address will be selected automatically for future orders
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-4 pt-8 border-t border-gray-200 mt-8">
                <button 
                  type="button" 
                  onClick={hideForm} 
                  disabled={actionStates.save} 
                  className="px-6 py-3 text-sm font-medium text-gray-700 bg-white border-2 border-gray-300 rounded-xl hover:bg-gray-50 hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  disabled={actionStates.save} 
                  className="inline-flex justify-center items-center px-8 py-3 text-sm font-medium text-white bg-gray-800 border-2 border-gray-800 rounded-xl hover:bg-gray-900 hover:border-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  {actionStates.save ? (
                    <>
                      <ArrowPathIcon className="animate-spin w-4 h-4 mr-2" /> 
                      Saving...
                    </>
                  ) : (
                    <>
                      {editingAddress ? (
                        <>
                          <PencilIcon className="w-4 h-4 mr-2" />
                          Save Changes
                        </>
                      ) : (
                        <>
                          <PlusIcon className="w-4 h-4 mr-2" />
                          Save Address
                        </>
                      )}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {addresses.length > 0 ? addresses.map((address) => (
              <div key={address.id} className="group relative bg-white rounded-2xl shadow-sm border border-gray-100 hover:shadow-lg hover:border-gray-200 transition-all duration-300 overflow-hidden">
                {/* Header with gradient background */}
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2 sm:space-x-3">
                      <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center shadow-sm ${
                        address.is_default 
                          ? 'bg-gradient-to-br from-blue-500 to-indigo-600 text-white' 
                          : 'bg-white border border-gray-200 text-gray-600'
                      }`}>
                        {address.is_default ? (
                          <StarIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                        ) : (
                          <HomeIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                        )}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 text-sm sm:text-lg">{address.name}</h3>
                        {address.is_default && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mt-1">
                            <StarIcon className="w-2.5 h-2.5 sm:w-3 sm:h-3 mr-1" />
                            <span className="text-xs">Default</span>
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Action buttons */}
                    <div className="flex items-center space-x-1 sm:space-x-2">
                      <button 
                        onClick={() => handleEdit(address)} 
                        className="p-1.5 sm:p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200 group-hover:opacity-100 opacity-70"
                        title="Edit address"
                      >
                        <PencilIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      </button>
                      <button 
                        onClick={() => handleDelete(address.id)} 
                        disabled={actionStates[`delete-${address.id}`]} 
                        className="p-1.5 sm:p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200 disabled:opacity-50 group-hover:opacity-100 opacity-70"
                        title="Delete address"
                      >
                        {actionStates[`delete-${address.id}`] ? (
                          <ArrowPathIcon className="animate-spin w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        ) : (
                          <TrashIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Address content */}
                <div className="p-4 sm:p-6">
                  <div className="space-y-2 sm:space-y-3">
                    <div className="flex items-start space-x-2 sm:space-x-3">
                      <MapPinIcon className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div className="space-y-1 flex-1">
                        <p className="text-gray-900 font-medium leading-relaxed text-sm sm:text-base">
                          {address.address_line_1}
                          {address.address_line_2 && (
                            <span className="block text-gray-600 font-normal text-xs sm:text-sm">{address.address_line_2}</span>
                          )}
                        </p>
                        <p className="text-gray-600 text-xs sm:text-sm">
                          {address.township}
                          {address.state_region && `, ${address.state_region}`}
                          {address.postal_code && ` ${address.postal_code}`}
                        </p>
                        <p className="text-gray-500 text-xs sm:text-sm font-medium">{address.country}</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Footer actions */}
                  <div className="mt-4 sm:mt-6 pt-3 sm:pt-4 border-t border-gray-100">
                    {!address.is_default ? (
                      <button 
                        onClick={() => handleSetDefault(address.id)} 
                        disabled={actionStates[`default-${address.id}`]} 
                        className="inline-flex items-center px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {actionStates[`default-${address.id}`] ? (
                          <>
                            <ArrowPathIcon className="animate-spin w-3 h-3 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />
                            <span className="text-xs sm:text-sm">Setting...</span>
                          </>
                        ) : (
                          <>
                            <StarIcon className="w-3 h-3 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />
                            <span className="text-xs sm:text-sm">Set as default</span>
                          </>
                        )}
                      </button>
                    ) : (
                      <div className="inline-flex items-center px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-medium text-green-700 bg-green-50 rounded-lg">
                        <CheckCircleIcon className="w-3 h-3 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />
                        <span className="text-xs sm:text-sm">Primary address</span>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Subtle gradient overlay for visual depth */}
                <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-gray-50/30 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
            )) : (
              <div className="col-span-full">
                <div className="text-center py-16">
                  <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <MapPinIcon className="w-10 h-10 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No addresses found</h3>
                  <p className="text-gray-500 mb-6">You haven't added any delivery addresses yet.</p>
                  <button 
                    onClick={handleAddNew}
                    className="inline-flex items-center px-6 py-3 text-sm font-medium text-white bg-gray-800 hover:bg-gray-900 rounded-lg transition-colors duration-200 shadow-sm"
                  >
                    <PlusIcon className="w-4 h-4 mr-2" />
                    Add your first address
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AddressManager;
